import React, { useMemo, useState } from 'react';
import { Platform, Text, TouchableOpacity, ActivityIndicator, View } from 'react-native';
import { useAccessibilityProps } from '../../../../shared/hooks/useAccessibilityProps';
import { ButtonProps } from '../../../../shared/components/ui/Button/ButtonProps';
import {
  buttonSizes,
  buttonVariants,
} from '../../../../shared/constants/global';
import Colors from '../../../../shared/theme/colors';
import { useScreenTheme } from '../../../../shared/hooks/useScreenTheme';
import { createButtonStyles } from '../../../../shared/components/ui/Button/Button.styles';

// ─── Web-branch module-level helpers (no nesting bonus) ──────────────────────

/** Activates the button on Enter/Space, matching native <button> behaviour. CC ≈ 3 */
function handleWebKeyDown(event: any, isDisabled: boolean, onPress: any): void {
  if (isDisabled) return;
  if (event.key === 'Enter' || event.key === ' ') {
    event.preventDefault?.();
    onPress(event);
  }
}

/** Sets isHovered only when not disabled. CC ≈ 1 */
function handleWebMouseEnter(isDisabled: boolean, setIsHovered: (v: boolean) => void): void {
  if (!isDisabled) setIsHovered(true);
}

/** Sets isPressed only when not disabled. CC ≈ 1 */
function handleWebMouseDown(isDisabled: boolean, setIsPressed: (v: boolean) => void): void {
  if (!isDisabled) setIsPressed(true);
}

/** Extracts aria-selected from accessibilityState safely. CC ≈ 2 */
function getAriaSelected(
  accessibilityState?: {selected?: boolean; [key: string]: any},
): boolean | undefined {
  return accessibilityState && 'selected' in accessibilityState
    ? accessibilityState.selected
    : undefined;
}

interface BuildButtonWebStylesOptions {
  styles: ReturnType<typeof createButtonStyles>;
  size: string;
  variant: string;
  fullWidth: boolean;
  containerStyle: any;
  isSecondary: boolean;
  isPrimary: boolean;
  isHovered: boolean;
  isPressed: boolean;
  isDisabled: boolean;
  isSelected: boolean;
  customDisabledContainerStyle: any;
}

/** Builds the web TouchableOpacity style array. CC ≈ 5 */
function buildButtonWebStyles(opts: BuildButtonWebStylesOptions): any[] {
  const {
    styles, size, variant, fullWidth, containerStyle,
    isSecondary, isPrimary, isHovered, isPressed, isDisabled, isSelected,
    customDisabledContainerStyle,
  } = opts;
  return [
    styles.base,
    (styles as any)[size],
    fullWidth && styles.fullWidth,
    (styles as any)[variant],
    containerStyle,
    isSecondary && isHovered && !isDisabled && !isSelected && styles.secondaryHovered,
    isSecondary && isPressed && !isDisabled && !isSelected && styles.secondaryPressed,
    isPrimary && isHovered && !isDisabled && !isSelected && styles.primaryHovered,
    isPrimary && isPressed && !isDisabled && !isSelected && styles.primaryPressed,
    isDisabled && [styles.disabled, customDisabledContainerStyle],
  ];
}

// ─── Shared inner content ────────────────────────────────────────────────────
// Renders the loading indicator OR the icon + text + subtitle row.
// Used by both the web and native branches of Button to eliminate duplication.
interface ButtonContentProps {
  loading: boolean;
  variant: string;
  customIconStyles?: any;
  showLeftIcon: boolean;
  isDisabled: boolean;
  customLeftIcon?: React.ReactNode;
  ctaDisableText: string;
  styles: ReturnType<typeof createButtonStyles>;
  customDisabledContainerText?: any;
  customTextStyle?: any;
  title: string;
  subTitle?: string;
  customSubTitleStyle?: any;
  /** When provided, replaces styles.subTitleHoveredBadge on hover so the caller
   *  can keep the selected badge colour rather than the default inverted hover colour. */
  customSubTitleHoveredStyle?: any;
  customSubTitleTextStyle?: any;
  showRightIcon: boolean;
  customRightIcon?: React.ReactNode;
  isHovered?: boolean;
}

const ButtonContent: React.FC<ButtonContentProps> = ({
  loading,
  variant,
  customIconStyles,
  showLeftIcon,
  isDisabled,
  customLeftIcon,
  ctaDisableText,
  styles,
  customDisabledContainerText,
  customTextStyle,
  title,
  subTitle,
  customSubTitleStyle,
  customSubTitleHoveredStyle,
  customSubTitleTextStyle,
  showRightIcon,
  customRightIcon,
  isHovered = false,
}) => {
  if (loading) {
    return (
      <ActivityIndicator
        color={variant === buttonVariants.primary ? Colors.white : Colors.black}
      />
    );
  }
  return (
    <View style={[styles.buttonTextContainer, customIconStyles]}>
      {showLeftIcon && (
        <View style={styles.iconWrapper}>
          {isDisabled && customLeftIcon
            ? React.cloneElement(customLeftIcon as React.ReactElement<any>, {color: ctaDisableText})
            : customLeftIcon}
        </View>
      )}
      <Text
        style={[
          styles.textBase,
          styles.textTruncate,
          variant === buttonVariants.primary
            ? styles.textPrimary
            : styles.textSecondary,
          isDisabled && [styles.textDisabled, customDisabledContainerText],
          customTextStyle,
        ]}
        numberOfLines={1}
      >
        {title}
      </Text>
      {subTitle ? (
        <View style={[styles.subTitleBadge, customSubTitleStyle, isHovered && (customSubTitleHoveredStyle ?? styles.subTitleHoveredBadge)]}>
          <Text style={[styles.subTitleText, customSubTitleTextStyle]}>{subTitle}</Text>
        </View>
      ) : null}
      {showRightIcon && (
        <View style={styles.iconWrapper}>{customRightIcon}</View>
      )}
    </View>
  );
};

/**
 * Reusable Button component with support for:
 * - Variants (primary, secondary, etc.)
 * - Sizes (small, medium, large)
 * - Disabled and loading states
 * - Full width option
 */
const Button: React.FC<ButtonProps> = ({
  title,
  subTitle,
  customSubTitleStyle,
  customSubTitleHoveredStyle,
  customSubTitleTextStyle,
  onPress,
  variant = buttonVariants.primary,
  size = buttonSizes.large,
  disabled = false,
  loading = false,
  fullWidth = true,
  containerStyle,
  customTextStyle,
  showRightIcon = false,
  showLeftIcon = false,
  customLeftIcon,
  customRightIcon,
  customIconStyles,
  accessibilityLabel,
  accessibilityHint,
  accessibilityRole = 'button',
  accessibilityState,
  accessible = true,
  testID,
  focusable = true,
  customDisabledContainerStyle,
  customDisabledContainerText
}) => {
  const isDisabled = disabled || loading;
  const theme = useScreenTheme('button');
  const styles = useMemo(() => createButtonStyles(theme), [theme]);
  const a11yProps = useAccessibilityProps({
    accessibilityLabel: accessibilityLabel || title,
    accessibilityHint,
  });

  // Hooks must be called unconditionally (Rules of Hooks).
  // isHovered / isPressed are only used in the web branch but must be declared
  // at the top level so every render calls them in the same order.
  const [isHovered, setIsHovered] = useState(false);
  const [isPressed, setIsPressed] = useState(false);

  // ─── Web: TouchableOpacity with explicit keyboard handler ───────────────────
  // On web, React Native Web renders TouchableOpacity as a <div>. While it is
  // focusable and responds to mouse clicks, it does NOT natively respond to
  // Enter/Space keyboard events (those are button-specific browser behaviours).
  // We fix this by attaching an onKeyDown handler directly on the TouchableOpacity
  // so that screen-reader users can activate the button with the keyboard.
  if (Platform.OS === 'web') {
    const isSecondary = variant === 'secondary';
    const isPrimary = variant === 'primary';
    const isSelected = accessibilityState?.selected === true;
    const effectiveLabel = accessibilityLabel || title;
    const disabledSuffix = isDisabled ? ', dimmed' : '';
    const hintSuffix = accessibilityHint ? `, ${accessibilityHint}` : '';
    const ariaLabel = `${effectiveLabel}${disabledSuffix}${hintSuffix}`;
    const ariaSelected = getAriaSelected(accessibilityState);

    return (
      <TouchableOpacity
        activeOpacity={isDisabled ? 1 : 0.8}
        onPress={isDisabled ? undefined : onPress}
        // @ts-ignore — onKeyDown, onMouseEnter, onMouseLeave, onMouseDown, onMouseUp are web-only events
        onKeyDown={(e: any) => handleWebKeyDown(e, isDisabled, onPress)}
        onMouseEnter={() => handleWebMouseEnter(isDisabled, setIsHovered)}
        onMouseLeave={() => { setIsHovered(false); setIsPressed(false); }}
        onMouseDown={() => handleWebMouseDown(isDisabled, setIsPressed)}
        onMouseUp={() => setIsPressed(false)}
        disabled={false}
        accessible={true}
        accessibilityRole={accessibilityRole}
        accessibilityLabel={ariaLabel}
        accessibilityState={{disabled: isDisabled, ...accessibilityState}}
        testID={testID}
        focusable={true}
        // @ts-ignore — tabIndex and aria-selected are web-only props
        tabIndex={0}
        // @ts-ignore
        aria-selected={ariaSelected}
        style={buildButtonWebStyles({
          styles, size, variant, fullWidth, containerStyle,
          isSecondary, isPrimary, isHovered, isPressed, isDisabled, isSelected,
          customDisabledContainerStyle,
        })}
      >
        <ButtonContent
          loading={loading}
          variant={variant}
          customIconStyles={customIconStyles}
          showLeftIcon={showLeftIcon}
          isDisabled={isDisabled}
          customLeftIcon={customLeftIcon}
          ctaDisableText={theme.ctaDisableText}
          styles={styles}
          customDisabledContainerText={customDisabledContainerText}
          customTextStyle={customTextStyle}
          title={title}
          subTitle={subTitle}
          customSubTitleStyle={customSubTitleStyle}
          customSubTitleHoveredStyle={customSubTitleHoveredStyle}
          customSubTitleTextStyle={customSubTitleTextStyle}
          showRightIcon={showRightIcon}
          customRightIcon={customRightIcon}
          isHovered={isHovered}
        />
      </TouchableOpacity>
    );
  }

  // ─── Native (iOS / Android): keep existing TouchableOpacity behaviour ────────
  const handlePress = (event: any) => {
    if (!isDisabled) {
      onPress(event);
    }
  };

  return (
    <TouchableOpacity
      activeOpacity={isDisabled ? 1 : 0.8}
      onPress={handlePress}
      disabled={isDisabled}
      accessible={accessible}
      accessibilityRole={accessibilityRole}
      {...a11yProps}
      accessibilityState={{ disabled: isDisabled, ...accessibilityState }}
      testID={testID}
      focusable={focusable ?? true}
      style={[
        styles.base,
        styles[size],
        fullWidth && styles.fullWidth,
        styles[variant],
        containerStyle,
        isDisabled && [styles.disabled, customDisabledContainerStyle],
      ]}
    >
      <ButtonContent
        loading={loading}
        variant={variant}
        customIconStyles={customIconStyles}
        showLeftIcon={showLeftIcon}
        isDisabled={isDisabled}
        customLeftIcon={customLeftIcon}
        ctaDisableText={theme.ctaDisableText}
        styles={styles}
        customDisabledContainerText={customDisabledContainerText}
        customTextStyle={customTextStyle}
        title={title}
        subTitle={subTitle}
        customSubTitleStyle={customSubTitleStyle}
        customSubTitleHoveredStyle={customSubTitleHoveredStyle}
        customSubTitleTextStyle={customSubTitleTextStyle}
        showRightIcon={showRightIcon}
        customRightIcon={customRightIcon}
      />
    </TouchableOpacity>
  );
};

export default Button;
